/**
 * The Moonlight chip: ten segments and a bare percentage.
 *
 * Presentation only. It reads an authoritative status object and paints it; it
 * decides nothing and gates nothing.
 *
 * IT REPLACED A 20px LUNAR DIAL, and the owner's reasons for rejecting that
 * are what the pips answer: the dial's unlit face was dark purple on a
 * near-black pill so a low value simply vanished; it was the same size and
 * shape as the Stardust chip beside it so it read as a second currency; and
 * nothing happened on hover so nothing said it was a control. Ten pips are
 * countable without reading the number, every unlit pip stays a visible
 * object, the row is long where its neighbours are round, and hover answers.
 *
 * THREE RULES, each of which this codebase has broken before in some shape:
 *
 *  1. UNKNOWN is a state, never an empty row. A Convex read that silently
 *     answers empty must not render as 0% — that is a wall in front of a
 *     player who has plenty.
 *  2. It never removes, hides or disables a play button. A readiness gate may
 *     not delete the UI it gates.
 *  3. It carries no words. The pips supply the noun, which is what keeps it
 *     narrow in the most contended lane on a phone and immune to German and
 *     CJK width. The unit is spoken only to screen readers.
 */

const EM_DASH = '—';

/** Fired by `convex-client.js` whenever the attached Convex identity changes. */
const AUTH_EVENT = 'moonfall:convex-auth-changed';
/**
 * Anything that MOVES the Moonlight balance says so with this event.
 *
 * The chip used to re-read only on mount and on an auth change, so a grant
 * landed in the database and the chip beside it kept showing the old figure
 * until the next reload. The owner hit it with a settled referral: "the 5 usd
 * worth of moonligth had no quest, no announcement, no visible reward."
 *
 * Deliberately an event rather than a direct call: the referral ceremony, a
 * Refill, a plan upgrade and the store all move this balance, and none of them
 * should have to know who is currently rendering it.
 */
const MOONLIGHT_CHANGED_EVENT = 'moonfall:moonlight-changed';
/** A plan change can replace the allowance and reset clock on a known chip. */
const SUBSCRIPTION_CHANGED_EVENT = 'moonfall:subscription-changed';
/** `i18n.js`: the translations arriving, and the language changing. */
const LANGUAGE_EVENTS = Object.freeze(['moonfall:i18nready', 'moonfall:languagechange']);
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * The bar is TIER-COLOURED, so the same percentage reads richer on a bigger
 * plan. The owner's point: "even if number might be same it has more juice".
 *
 * Free is cold silver, Storyteller a brighter blue-white, Manga a warm
 * champagne. The two STATE signals — manga armed, and overcharged above one
 * cycle — deliberately stay modifiers of the tier hue rather than colours of
 * their own, because a state that repaints the bar in another tier's colour
 * would make the tier unreadable.
 */
const TIER_CLASS = Object.freeze({
  free: 'is-tier-free',
  moonfall_storyteller: 'is-tier-storyteller',
  moonfall_manga: 'is-tier-manga',
});
const TIER_CLASSES = Object.freeze(Object.values(TIER_CLASS));

/** Ten pips: one per tenth of a cycle. */
const SEGMENT_COUNT = 10;

/* THE LAST STATUS A MOUNTED CHIP RESOLVED — the lobby chip's — or null while
   it knows nothing. The store's meter opens at this value instead of an
   em-dash: the same account, painted by the same renderer, so the two agree
   from the store's first frame. Follows the chip exactly, so a sign-out that
   clears the chip clears this too. */
let lastMountedStatus = null;

function peekMoonlightStatus() {
  return lastMountedStatus;
}

function isFinitePercent(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function clamp01(value) {
  return Math.min(1, Math.max(0, Number(value) || 0));
}

/* Written only when it differs. A re-read that finds the value already shown
   must leave the chip's nodes exactly as they are — a repaint reconciles, it
   never rebuilds — and a class toggled to the state it already has is free. */
function setText(node, text) {
  if (node && node.textContent !== text) node.textContent = text;
}

function setVar(element, name, value) {
  if (value === null) {
    if (element.style.getPropertyValue(name) !== '') element.style.removeProperty(name);
    return;
  }
  if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
}

/* A pip's pass, as classes the stylesheet paints (auth.css): lit (the first
   cycle), surplus (the second), gem (the third, fifth…) and supercharged (the
   fourth on). Each pass whitens the fill and widens the tier-coloured glow, so
   every further cycle reads as MORE rather than as a colour repeating. From
   the fifth pass on the look holds at its brightest and the numeral carries
   the figure: a sixth pass painted as a fourth made 570% read dimmer than
   500%. */
const BRIGHTEST_PASS = 5;

function setLap(segment, lap) {
  const value = String(lap);
  if (segment.dataset.lap !== value) segment.dataset.lap = value;
  const look = Math.min(lap, BRIGHTEST_PASS);
  segment.classList.toggle('is-lit', look >= 1);
  segment.classList.toggle('is-surplus', look >= 2);
  segment.classList.toggle('is-gem', look >= 3 && look % 2 === 1);
  segment.classList.toggle('is-supercharged', look >= 4);
}

/* Far below a pip's worth, so a quotient that lands a hair off a whole cycle
   (2.0000000001) is read as the whole cycle it is. */
const LAP_EPSILON = 1e-9;

/**
 * Which pass each pip shows: 0 dark, 1 the tier's own colour, then one more
 * for every further cycle held.
 *
 * EVERY 100% PAINTS THE BAR AGAIN (the owner, 2026-09-24: "it should keep
 * increasing in the color"). The surplus used to stop at 200% — all ten pips
 * charged — so 229% looked exactly like 200%. Now the top pass covers its
 * share of the ten pips and the pass beneath it is full: 140% is four
 * second-pass pips over the tier colour, 229% three third-pass pips over a full
 * second pass. ROUNDED, like the first pass, so the pips never contradict the
 * numeral, and any share at all keeps one pip — 201% is not 200%.
 */
function pipLaps(total) {
  if (!(total > 0)) return new Array(SEGMENT_COUNT).fill(0);
  const whole = total <= 1 ? 0 : Math.floor(total + LAP_EPSILON);
  const share = total - whole;
  const top = share > LAP_EPSILON
    ? Math.min(SEGMENT_COUNT, Math.max(1, Math.round(share * SEGMENT_COUNT)))
    : 0;
  return Array.from({ length: SEGMENT_COUNT }, (_unused, index) => (index < top ? whole + 1 : whole));
}

/**
 * `pending`: no read has answered yet. That is not UNKNOWN. Unknown is an
 * answer — a read that failed or could not say — and the chip shows it as one
 * (the em-dash, the neutral row, the whole chip at 40%). Before any answer the
 * chip shows its frame and no figure at all (`is-pending`, auth.css): painting
 * the unknown state there made every sign-in show a failed meter that then
 * brightened in place, "—" turning into "95%" (17 October 2026).
 */
function renderMoonlightChip(element, status, { label, pending = false } = {}) {
  if (!element) return;

  // Applied in every state, including unknown: the tier is a fact about the
  // account, not about whether this particular read succeeded.
  const tierClass = TIER_CLASS[String(status?.subscriptionType || '')] || TIER_CLASS.free;
  TIER_CLASSES.forEach((name) => element.classList.toggle(name, name === tierClass));

  const value = element.querySelector('.moonlight-value');
  const segments = element.querySelectorAll('.moonlight-seg');
  const known = status
    && status.state === 'known'
    && isFinitePercent(status.percent)
    && isFinitePercent(status.fraction);

  // Visible in EVERY state. A state light that disappears when the state is
  // bad is the gate-deletes-UI bug wearing a different hat.
  if (element.hidden) element.hidden = false;
  element.classList.toggle('is-pending', !known && pending);
  element.classList.toggle('is-unknown', !known && !pending);


  if (!known) {
    setText(value, pending ? '' : EM_DASH);
    setVar(element, '--moonlight-halo', null);
    setVar(element, '--moonlight-cycles', null);
    // Not "zero lit": an unknown read renders as a neutral row, which is a
    // shape no real value produces.
    segments.forEach((segment) => setLap(segment, 0));
    element.classList.remove('is-manga-armed');
    // A failed read is not a surplus. Leaving the overcharge on would keep a
    // stale claim of abundance glowing over an unknown value.
    element.classList.remove('is-overcharged');
    return;
  }

  const percent = Math.max(0, Math.round(status.percent));
  setText(value, `${percent}%`);

  /* One number for the whole bar: the first cycle, then whatever sits above
     it. The pips are its passes (`pipLaps`); the first pass keeps its old
     rule — any Moonlight at all keeps one pip alight, because a lit row that
     reads 3% and an empty one that reads 0% are different facts about
     whether you can still play. */
  const fraction = clamp01(status.fraction);
  const surplusRaw = Number(status.haloFraction);
  const surplusCycles = fraction >= 1 && Number.isFinite(surplusRaw) && surplusRaw > 0 ? surplusRaw : 0;
  const total = fraction >= 1 ? 1 + surplusCycles : fraction;
  const passes = pipLaps(total);
  segments.forEach((segment, index) => setLap(segment, passes[index] || 0));

  /* Anything over one cycle also glows around the whole row — abundance,
     never overflow. The glow reaches its height at 200%; the passes carry
     everything above that. */
  const halo = clamp01(surplusCycles);
  setVar(element, '--moonlight-halo', String(halo));
  setVar(element, '--moonlight-cycles', String(Math.floor(total + LAP_EPSILON)));
  element.classList.toggle('is-overcharged', halo > 0);

  // Brighter while manga is armed for the next duel.
  element.classList.toggle('is-manga-armed', Boolean(status.mangaArmed));

  const aria = label || `Moonlight, ${percent} percent`;
  if (element.getAttribute('aria-label') !== aria) element.setAttribute('aria-label', aria);
}

/**
 * MOUNT THE CHIP AND KEEP IT HONEST ACROSS THE AUTH RACE.
 *
 * `usageCaps:getMoonlightStatus` answers an unauthenticated caller with
 * `{ state: 'unknown' }`. That is the right answer — but the Convex auth token
 * is attached separately from the boot render, so a read taken a beat too early
 * is anonymous, and the chip painted an em-dash for the rest of the session on
 * an account with a full moon. Nothing re-read, because there was no signal to
 * re-read on.
 *
 * There is one now. An unresolved chip re-reads when an identity lands; a
 * resolved one does not, because a refreshed token is not new information about
 * a value already known. A read that is STILL unknown once signed in keeps its
 * em-dash: that is a real failure and must not be dressed up as 0%.
 *
 * @param {HTMLElement} element
 * @param {{ read?: (options: { force: boolean }) => (Promise<any>|any), label?: (status: any) => string }} [options]
 * @returns {{ ready: Promise<void>, refresh: (options?: { force?: boolean }) => Promise<void>, getStatus: () => any, dispose: () => void }}
 */
function mountMoonlightChip(element, { read, label } = {}) {
  let status = null;
  // No read has answered yet: the chip is pending, not unknown.
  let answered = false;
  let ready = Promise.resolve();
  let disposed = false;
  let requestId = 0;
  let pending = false;
  let pendingForced = false;
  let resetTimer = null;
  let attemptedResetAt = null;

  const clearResetTimer = () => {
    if (resetTimer !== null) clearTimeout(resetTimer);
    resetTimer = null;
  };

  const resetAt = () => {
    const at = Number(status?.returnsAt);
    return Number.isFinite(at) && at > 0 ? at : null;
  };

  const refreshIfDue = () => {
    const at = resetAt();
    if (disposed || at === null || at > Date.now() || (pending && pendingForced)) return;
    attemptedResetAt = at;
    void refresh({ force: true });
  };

  const scheduleReset = () => {
    clearResetTimer();
    const at = resetAt();
    if (disposed || at === null || (at <= Date.now() && attemptedResetAt === at)) return;
    resetTimer = setTimeout(() => {
      resetTimer = null;
      if (at > Date.now()) scheduleReset();
      else refreshIfDue();
    }, Math.max(0, Math.min(at - Date.now(), MAX_TIMEOUT_MS)));
  };

  const paint = () => {
    lastMountedStatus = status;
    const options = { pending: !answered };
    if (typeof label === 'function') {
      const text = label(status);
      if (text) options.label = String(text);
    }
    renderMoonlightChip(element, status, options);
  };

  const refresh = ({ force = false } = {}) => {
    if (disposed) return ready;
    const request = ++requestId;
    pending = true;
    pendingForced = force;
    ready = Promise.resolve()
      .then(() => {
        if (disposed || request !== requestId) return null;
        return typeof read === 'function' ? read({ force }) : null;
      })
      .then((next) => {
        if (disposed || request !== requestId) return;
        const answer = next && next.state === 'known' ? next : null;
        /* A value already shown is kept when a RE-read cannot confirm it (a
           flaky link, a server hiccup): withdrawing it painted the em-dash
           and darkened every pip until the next event. Only a sign-out
           (onIdentity) or a chip that never resolved shows unknown. */
        if (!answer && status) return;
        status = answer;
        answered = true;
        paint();
      })
      .catch((err) => {
        if (disposed || request !== requestId) return;
        console.warn('[Moonlight] Could not resolve Moonlight status:', err);
        if (status) return;
        status = null;
        answered = true;
        paint();
      })
      .finally(() => {
        if (disposed || request !== requestId) return;
        pending = false;
        pendingForced = false;
        // A failed or not-yet-current answer keeps its displayed value. Never
        // retry an already attempted date: retry on return, without a loop.
        scheduleReset();
      });
    return ready;
  };

  const onIdentity = (event) => {
    if (disposed) return;
    if (event && event.detail && event.detail.authenticated === false) {
      // Never leave a previous account's balance lit on a signed-out chip.
      requestId += 1;
      pending = false;
      pendingForced = false;
      attemptedResetAt = null;
      clearResetTimer();
      status = null;
      // Signed out is an answer: the chip says it does not know.
      answered = true;
      paint();
      return;
    }
    if (status) return;
    refresh();
  };

  /* A grant is authoritative and already committed, so re-read unconditionally
     — unlike `onIdentity`, which skips when a status is already held. */
  const onMoonlightChanged = () => { if (!disposed) refresh(); };
  const onSubscriptionChanged = () => { if (!disposed) refresh({ force: true }); };
  const onVisible = () => { if (document.visibilityState === 'visible') refreshIfDue(); };

  /* ITS NAME FOLLOWS THE LANGUAGE. `label` is written from the translations,
     and the boot read usually lands before ui.json does, so the chip was named
     from the fallback and kept it for the session — or after a language
     switch. A language is not a new balance: this repaints, it does not
     re-read. */
  const onLanguage = () => { if (!disposed) paint(); };

  paint();
  refresh();
  if (typeof window !== 'undefined') {
    window.addEventListener(AUTH_EVENT, onIdentity);
    window.addEventListener(MOONLIGHT_CHANGED_EVENT, onMoonlightChanged);
    window.addEventListener(SUBSCRIPTION_CHANGED_EVENT, onSubscriptionChanged);
    window.addEventListener('focus', refreshIfDue);
    document.addEventListener('visibilitychange', onVisible);
    for (const event of LANGUAGE_EVENTS) window.addEventListener(event, onLanguage);
  }

  return {
    get ready() { return ready; },
    refresh,
    getStatus: () => status,
    dispose: () => {
      disposed = true;
      requestId += 1;
      clearResetTimer();
      if (typeof window === 'undefined') return;
      window.removeEventListener(AUTH_EVENT, onIdentity);
      window.removeEventListener(MOONLIGHT_CHANGED_EVENT, onMoonlightChanged);
      window.removeEventListener(SUBSCRIPTION_CHANGED_EVENT, onSubscriptionChanged);
      window.removeEventListener('focus', refreshIfDue);
      document.removeEventListener('visibilitychange', onVisible);
      for (const event of LANGUAGE_EVENTS) window.removeEventListener(event, onLanguage);
    },
  };
}

export {
  renderMoonlightChip, mountMoonlightChip, peekMoonlightStatus, SEGMENT_COUNT, TIER_CLASS,
  MOONLIGHT_CHANGED_EVENT,
};
